import { CANON } from "@/lib/canon.generated";
import { EXPORT } from "@/lib/export.generated";

/**
 * What a film is made of, as plain arithmetic.
 *
 * Kept free of React and of the DOM so it can be tested as functions, and
 * ported from iOS's `ExportSettings` rather than invented: the two clients
 * produce the same film from the same album, or the feature is two features
 * that look alike.
 */

/** How far back the film reaches. */
export type Range = "all" | "lastYear" | "last90Days";

export const RANGES: readonly Range[] = ["all", "lastYear", "last90Days"] as const;

/**
 * How long the finished video should run, roughly. People think in seconds;
 * nobody thinks in frames per second.
 *
 * Every length is free. The rate is floored, so an album under
 * `length × floor` frames comes out at the same running time whichever is
 * picked — a diary of 100 days answers 15, 45 and 90 alike with a
 * 12.5-second film. That is not a trap as long as the screen says so, and it
 * does: each option carries `durationSeconds` underneath, recomputed from
 * the days actually selected.
 */
export const LENGTHS = [15, 30, 45, 60, 90] as const;
export type Length = (typeof LENGTHS)[number];

/** The shape of the finished frame. The canon is portrait 2:3. */
export type Frame = "asTaken" | "stories" | "square";

export const FRAMES: readonly Frame[] = ["asTaken", "stories", "square"] as const;

/** Width over height of the finished canvas. */
export function frameAspect(frame: Frame): number {
  switch (frame) {
    case "stories":
      return 9 / 16;
    case "square":
      return 1;
    default:
      return CANON.aspect;
  }
}

/**
 * `mark`: the "Selfeed" wordmark along the bottom of every frame. On for
 * everybody, the way the phone's is; only Pro is offered the switch, and
 * the phone's switch starts on too.
 */
export type Settings = { range: Range; length: Length; frame: Frame; mark: boolean };

export const DEFAULT_SETTINGS: Settings = {
  range: "all",
  length: 30,
  frame: "asTaken",
  mark: true,
};

/**
 * Whether two sets of settings would produce the same film.
 *
 * A field-by-field comparison rather than an identity check: the store holds
 * the settings a finished film was made with, and what matters is whether
 * they still describe the film on offer — not whether they are the same
 * object, which they never are.
 */
export function sameSettings(one: Settings, two: Settings): boolean {
  return (
    one.range === two.range &&
    one.length === two.length &&
    one.frame === two.frame &&
    one.mark === two.mark
  );
}

/** A local Y-M-D shifted by whole days, with no timezone able to move it. */
function shift(date: string, days: number): string {
  const [year = 0, month = 1, day = 1] = date.split("-").map(Number);
  const moved = new Date(year, month - 1, day + days);
  const y = moved.getFullYear();
  const m = String(moved.getMonth() + 1).padStart(2, "0");
  const d = String(moved.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * The days a range selects out of an album's own days.
 *
 * Counted back from the album's NEWEST frame, not from today — iOS's rule,
 * and the reason is that a diary somebody stopped keeping in March should
 * still produce a film when they ask for "the last ninety days", rather than
 * an empty one because ninety days from today reaches past everything they
 * have.
 *
 * `days` is expected newest-last (the order a calendar reads), and the
 * answer keeps that order: a film runs forwards through time.
 */
export function selectDays(days: readonly string[], range: Range): string[] {
  const newest = days.at(-1);
  if (!newest) return [];
  if (range === "all") return [...days];
  const floor = range === "lastYear" ? shift(newest, -365) : shift(newest, -90);
  // String comparison, not Date arithmetic: ISO Y-M-D sorts
  // lexicographically, so no hour of drift can move a day across the bound.
  return days.filter((day) => day >= floor);
}

/**
 * The rate that gets `frames` as close to the asked-for length as the bounds
 * allow.
 */
export function frameRate(frames: number, length: Length): number {
  if (frames <= 0) return EXPORT.frameRateFloor;
  const ideal = Math.round(frames / length);
  return Math.min(Math.max(ideal, EXPORT.frameRateFloor), EXPORT.frameRateCeiling);
}

/**
 * How long it will actually run — which is not what was asked for whenever a
 * bound bites. The screen shows this rather than the ask.
 */
export function durationSeconds(frames: number, length: Length): number {
  if (frames <= 0) return 0;
  return frames / frameRate(frames, length);
}

/**
 * The canvas the canon is composed into.
 *
 * The canon always fits inside at its own resolution and is never scaled up:
 * whichever side the target needs more of, the canvas grows on that side and
 * the remainder is filled. Cropping instead would cut a face out of a frame
 * the whole app exists to centre.
 *
 * Both sides are rounded to even numbers. Every codec worth using subsamples
 * chroma, and an odd dimension is either refused outright or silently padded
 * by one row of whatever was in the buffer.
 */
export function canvasSize(frame: Frame): { width: number; height: number } {
  const source = { width: CANON.width, height: Math.round(CANON.width / CANON.aspect) };
  const aspect = frameAspect(frame);
  const width = Math.max(source.width, Math.round(source.height * aspect));
  const height = Math.max(source.height, Math.round(width / aspect));
  return { width: even(width), height: even(height) };
}

function even(value: number): number {
  return value % 2 === 0 ? value : value + 1;
}

/**
 * Where the canon sits inside the canvas: centred, at its own size, never
 * scaled up.
 */
export function framePlacement(frame: Frame): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const canvas = canvasSize(frame);
  const width = CANON.width;
  const height = Math.round(CANON.width / CANON.aspect);
  return {
    x: Math.round((canvas.width - width) / 2),
    y: Math.round((canvas.height - height) / 2),
    width,
    height,
  };
}
